/**
 * StateSelect — the mailing-state control for the split-address mortgagee
 * form (operator design 2026-08-03): a dropdown whose rows read
 * `"Michigan | MI"`, filterable by typing into the trigger input.
 *
 * Rendered via JsonSchemaForm's `itemFieldOverrides` for
 * `mortgagees.mailing_state`. Deliberately a plain input + custom listbox,
 * NOT an MUI Autocomplete: typing into MUI Autocomplete is untestable in
 * this app's jest setup (dual React copies — see the jest landmine notes),
 * while a plain input types fine and the listbox commits by click.
 *
 * Commit rules — the committed value is ALWAYS a valid USPS code or "":
 * clicking a row commits its code; Enter commits the single remaining
 * filtered row; blur resolves exact text (code, name, or label) or reverts
 * to the last committed value. Free text never escapes this control.
 */

import { useEffect, useRef, useState } from "react";
import {
  filterStates,
  matchState,
  stateLabel,
  US_STATES,
} from "./addressParts";

export interface StateSelectProps {
  label: string;
  /** Committed USPS code ("" = none). */
  value: string;
  disabled?: boolean;
  onCommit: (code: string) => void;
  /** Unique per row — DOM ids must not collide across mortgagee cards. */
  idSuffix?: string;
}

function displayFor(code: string): string {
  const s = US_STATES.find((st) => st.code === code);
  return s ? stateLabel(s) : "";
}

export default function StateSelect({
  label,
  value,
  disabled,
  onCommit,
  idSuffix = "0",
}: StateSelectProps) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState<string>(() => displayFor(value));
  const rootRef = useRef<HTMLDivElement>(null);
  const inputId = `state-select-${idSuffix}`;

  // Parent-driven value changes (prefill arriving, row reset) refresh the
  // display; the user's in-progress filter text is only replaced when the
  // dropdown is closed, so typing is never clobbered mid-search.
  useEffect(() => {
    if (!open) setText(displayFor(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setText(displayFor(value));
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        setText(displayFor(value));
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, value]);

  const commit = (code: string) => {
    onCommit(code);
    setText(displayFor(code));
    setOpen(false);
  };

  const filtered = filterStates(text);

  return (
    <div className="flex flex-col gap-1" ref={rootRef}>
      <label htmlFor={inputId} className="flex items-center gap-1.5 text-xs font-bold text-primary-gray-light-700">
        {label}
      </label>
      <div className="relative">
        <input
          id={inputId}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${inputId}-listbox`}
          aria-autocomplete="list"
          autoComplete="off"
          value={text}
          disabled={disabled}
          placeholder="Type to filter states…"
          onFocus={(e) => {
            setOpen(true);
            e.target.select();
          }}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
            if (e.target.value.trim() === "") onCommit("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (filtered.length === 1) commit(filtered[0].code);
            }
          }}
          onBlur={() => {
            // Option clicks never reach here — their mousedown preventDefault
            // keeps focus in the input — so blur only means "left the field":
            // resolve exact text or revert to the last committed value.
            setOpen(false);
            const exact = matchState(text);
            if (exact) {
              onCommit(exact.code);
              setText(stateLabel(exact));
            } else if (text.trim() === "") {
              onCommit("");
              setText("");
            } else {
              setText(displayFor(value));
            }
          }}
          className="w-full touch-target rounded-lg border border-primary-gray-light-300 px-3 py-2 text-base bg-base-white text-primary-gray-light-900 disabled:bg-primary-gray-light-50 disabled:text-primary-gray-light-700 sm:text-sm"
          data-testid={`state-select-input-${idSuffix}`}
        />
        {open && !disabled && (
          <ul
            id={`${inputId}-listbox`}
            role="listbox"
            aria-label={label}
            className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-primary-gray-light-300 bg-base-white shadow-lg"
            data-testid={`state-select-listbox-${idSuffix}`}
          >
            {filtered.length === 0 && (
              <li className="px-3 py-1.5 text-sm text-primary-gray-light-500">
                No matching state
              </li>
            )}
            {filtered.map((s) => (
              <li key={s.code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={s.code === value}
                  className={`flex w-full touch-target items-center px-3 py-1.5 text-left text-sm hover:bg-primary-gray-light-100 ${
                    s.code === value ? "bg-primary-gray-light-50 font-medium" : ""
                  }`}
                  // mousedown fires before the input's blur — commit here so
                  // the blur handler sees the dropdown already closed.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    commit(s.code);
                  }}
                  data-testid={`state-select-option-${idSuffix}-${s.code}`}
                >
                  {stateLabel(s)}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
